import { Alert } from '@cmsgov/design-system';
import classnames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

import { Button, ButtonVariation } from '../Button';
import { ErrorIcon } from '../Icon';
import Modal from '../Modal/Modal';
import Radio from '../Radio/Radio';
import { RequiredFieldsLegend } from '../RequiredFieldsLegend';
import Tabs, { Tab } from '../Tabs/Tabs';
import TextField from '../TextField/TextField';
import Tooltip from '../Tooltip/Tooltip';
import { ModalSize } from '../../utils';

import { getAttestationDisclaimerText } from '../../utils/attestation-disclaimer';
import { buildSignatureAuditMetadata, recordSignatureAuditEvent } from '../../pages/application/utils/signature-audit';
import { useSignatureCanvasDimensions } from './useSignatureCanvasDimensions';

import './DigitalSignatureModal.css';

export enum SignatureStyle {
  OREGANO = 'oregano',
  DANCING_SCRIPT = 'dancing-script',
  GREAT_VIBES = 'great-vibes',
  PACIFICO = 'pacifico',
  SATISFY = 'satisfy',
}

const SIGNATURE_STYLES: Array<{ id: SignatureStyle; label: string; className: string }> = [
  { className: 'digital-signature-modal__preview-signature--oregano', id: SignatureStyle.OREGANO, label: 'Oregano' },
  {
    className: 'digital-signature-modal__preview-signature--dancing-script',
    id: SignatureStyle.DANCING_SCRIPT,
    label: 'Dancing Script',
  },
  {
    className: 'digital-signature-modal__preview-signature--great-vibes',
    id: SignatureStyle.GREAT_VIBES,
    label: 'Great Vibes',
  },
  { className: 'digital-signature-modal__preview-signature--pacifico', id: SignatureStyle.PACIFICO, label: 'Pacifico' },
  { className: 'digital-signature-modal__preview-signature--satisfy', id: SignatureStyle.SATISFY, label: 'Satisfy' },
];

export interface SignatureData {
  type: 'style' | 'draw';
  fullName: string;
  signatureId: string;
  browserFingerprint: string;
  fontFamily?: SignatureStyle;
  imageData?: string;
  timestamp: string;
}

export interface DigitalSignatureModalProps {
  applicationId?: string;
  fullName: string;
  isOpen: boolean;
  onClose: () => void;
  onAdoptAndSign: (signatureData: SignatureData) => void | Promise<void>;
  applicationTitle?: string;
  isSubmitting?: boolean;
  submitError?: string;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

type SignatureTabId = 'select-style' | 'draw';

function mapTabToSignatureType(tabId: SignatureTabId): 'drawn' | 'styled' {
  return tabId === 'draw' ? 'drawn' : 'styled';
}

function generateSignatureId(): string {
  const chars = 'ABCDEF0123456789';
  let result = '';

  for (let i = 0; i < 15; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return result;
}

async function generateBrowserFingerprint(): Promise<string> {
  const components: string[] = [];

  components.push(`${screen.width}x${screen.height}x${screen.colorDepth}`);
  components.push(Intl.DateTimeFormat().resolvedOptions().timeZone);
  components.push(navigator.language);
  components.push(navigator.platform);
  components.push(navigator.userAgent.length.toString());

  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('fingerprint', 2, 2);
      components.push(canvas.toDataURL().slice(-50));
    }
  } catch {
    components.push('no-canvas');
  }

  if (navigator.hardwareConcurrency) {
    components.push(navigator.hardwareConcurrency.toString());
  }

  const fingerprintString = components.join('|');
  const encoder = new TextEncoder();
  const data = encoder.encode(fingerprintString);

  try {
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

    return hashHex.substring(0, 32).toUpperCase();
  } catch {
    let hash = 0;

    for (let i = 0; i < fingerprintString.length; i++) {
      const char = fingerprintString.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }

    return Math.abs(hash).toString(16).toUpperCase().padStart(16, '0');
  }
}

function DigitalSignatureModal({
  applicationId,
  fullName,
  isOpen,
  onClose,
  onAdoptAndSign,
  applicationTitle = 'Application',
  isSubmitting = false,
  submitError = '',
  'data-testid': dataTestId,
}: DigitalSignatureModalProps) {
  const baseTestId = dataTestId ?? 'digital-signature-modal';
  const normalizedFullName = fullName.trim();

  const [selectedTab, setSelectedTab] = useState<SignatureTabId>('select-style');
  const [selectedStyle, setSelectedStyle] = useState<SignatureStyle>(SignatureStyle.OREGANO);
  const [signatureId] = useState(() => generateSignatureId());
  const [browserFingerprint, setBrowserFingerprint] = useState<string>('');

  const [fullNameError, setFullNameError] = useState<string>('');
  const [canvasError, setCanvasError] = useState<string>('');
  const [hasCanvasInput, setHasCanvasInput] = useState(false);
  const [drawnSignatureImage, setDrawnSignatureImage] = useState<string>('');

  const signatureCanvasRef = useRef<SignatureCanvas>(null);
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const hasRecordedOpenEventsRef = useRef(false);
  const canvasDimensions = useSignatureCanvasDimensions(canvasWrapperRef, isOpen && selectedTab === 'draw');

  useEffect(() => {
    if (!isOpen) {
      hasRecordedOpenEventsRef.current = false;

      return;
    }

    if (!applicationId || hasRecordedOpenEventsRef.current) {
      return;
    }

    hasRecordedOpenEventsRef.current = true;
    const openMetadata = buildSignatureAuditMetadata({
      signatureType: mapTabToSignatureType(selectedTab),
    });

    void recordSignatureAuditEvent(applicationId, 'SIGNATURE_MODAL_OPENED', openMetadata);
    void recordSignatureAuditEvent(applicationId, 'ATTESTATION_VIEWED', openMetadata);
    void recordSignatureAuditEvent(applicationId, 'SIGNATURE_TYPE_SELECTED', openMetadata);
  }, [applicationId, isOpen, selectedTab]);

  useEffect(() => {
    generateBrowserFingerprint().then(setBrowserFingerprint);
  }, []);

  useEffect(() => {
    if (normalizedFullName) {
      setFullNameError('');
    }
  }, [normalizedFullName]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      const nextTab = tabId as SignatureTabId;
      setSelectedTab(nextTab);
      setCanvasError('');

      if (applicationId) {
        void recordSignatureAuditEvent(
          applicationId,
          'SIGNATURE_TYPE_SELECTED',
          buildSignatureAuditMetadata({
            signatureType: mapTabToSignatureType(nextTab),
          }),
        );
      }
    },
    [applicationId],
  );

  const handleStyleSelect = useCallback(
    (style: SignatureStyle) => {
      setSelectedStyle(style);

      if (applicationId) {
        void recordSignatureAuditEvent(
          applicationId,
          'SIGNATURE_STYLE_SELECTED',
          buildSignatureAuditMetadata({
            signatureType: 'styled',
            styleIdentifier: style,
          }),
        );
      }
    },
    [applicationId],
  );

  const handleClearCanvas = useCallback(() => {
    signatureCanvasRef.current?.clear();
    setHasCanvasInput(false);
    setDrawnSignatureImage('');

    if (applicationId) {
      void recordSignatureAuditEvent(applicationId, 'SIGNATURE_CLEARED', buildSignatureAuditMetadata());
    }
  }, [applicationId]);

  const handleCanvasBeginStroke = useCallback(() => {
    setCanvasError('');
    setHasCanvasInput(true);
  }, []);

  const handleCanvasEndStroke = useCallback(() => {
    const canvas = signatureCanvasRef.current;

    if (canvas && !canvas.isEmpty()) {
      setDrawnSignatureImage(canvas.toDataURL('image/png'));

      if (applicationId) {
        void recordSignatureAuditEvent(
          applicationId,
          'SIGNATURE_DRAWN',
          buildSignatureAuditMetadata({
            canvasDimensions: {
              height: canvasDimensions.layoutHeight,
              width: canvasDimensions.layoutWidth,
            },
            inputDevice: 'ontouchstart' in window ? 'touch' : 'mouse',
            signatureType: 'drawn',
          }),
        );
      }
    }
  }, [applicationId, canvasDimensions.layoutHeight, canvasDimensions.layoutWidth]);

  const getSignatureData = useCallback((): SignatureData | null => {
    const baseData = {
      browserFingerprint,
      fullName: normalizedFullName,
      signatureId,
      timestamp: new Date().toISOString(),
    };

    switch (selectedTab) {
      case 'select-style':
        return {
          ...baseData,
          fontFamily: selectedStyle,
          type: 'style',
        };
      case 'draw': {
        const canvas = signatureCanvasRef.current;

        if (!canvas || canvas.isEmpty()) {
          return null;
        }

        return {
          ...baseData,
          imageData: canvas.toDataURL('image/png'),
          type: 'draw',
        };
      }
      default:
        return null;
    }
  }, [normalizedFullName, signatureId, browserFingerprint, selectedTab, selectedStyle]);

  const handleAdoptAndSign = useCallback(async () => {
    let hasErrors = false;

    if (!normalizedFullName) {
      setFullNameError('Full name is required. Complete your personal details before submitting.');
      hasErrors = true;
    }

    if (selectedTab === 'draw') {
      const canvas = signatureCanvasRef.current;

      if (!canvas || canvas.isEmpty()) {
        setCanvasError('Please draw your signature');
        hasErrors = true;
      }
    }

    if (hasErrors) {
      return;
    }

    const signatureData = getSignatureData();

    if (!signatureData) {
      return;
    }

    if (applicationId) {
      const adoptedMetadata = buildSignatureAuditMetadata({
        canvasFingerprint: signatureData.browserFingerprint,
        signatureType: signatureData.type === 'style' ? 'styled' : 'drawn',
        styleIdentifier: signatureData.fontFamily,
        timestamp: signatureData.timestamp,
      });

      await recordSignatureAuditEvent(applicationId, 'ATTESTATION_AGREED', adoptedMetadata);
      await recordSignatureAuditEvent(applicationId, 'SIGNATURE_ADOPTED', adoptedMetadata);
    }

    await onAdoptAndSign(signatureData);
  }, [applicationId, normalizedFullName, selectedTab, getSignatureData, onAdoptAndSign]);

  const currentStyleClass = useMemo(() => {
    const style = SIGNATURE_STYLES.find((s) => s.id === selectedStyle);

    return style?.className || '';
  }, [selectedStyle]);

  const formattedDate = useMemo(() => {
    return new Date().toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }, []);

  const disclaimerParagraphs = useMemo(() => {
    return getAttestationDisclaimerText(applicationTitle).split('\n');
  }, [applicationTitle]);

  const renderPreview = () => {
    return (
      <p className={classnames('digital-signature-modal__preview-signature', currentStyleClass)}>
        {normalizedFullName || 'Your Name'}
      </p>
    );
  };

  const renderStyleTab = () => (
    <fieldset
      className='digital-signature-modal__style-selection'
      data-testid={`${baseTestId}-style-selection`}>
      <legend className='ds-u-visibility--screen-reader'>Select signature style</legend>
      {SIGNATURE_STYLES.map((style) => (
        <Radio<SignatureStyle>
          key={style.id}
          checked={selectedStyle === style.id}
          className='digital-signature-modal__style-option'
          data-testid={`${baseTestId}-style-${style.id}`}
          label={style.label}
          labelContent={
            <span
              className={classnames('digital-signature-modal__style-option-preview', style.className)}
              data-testid={`${baseTestId}-style-preview-${style.id}`}
              id={`style-preview-${style.id}`}>
              {normalizedFullName || 'Your Name'}
            </span>
          }
          name='signature-style'
          value={style.id}
          onChange={() => handleStyleSelect(style.id)}
        />
      ))}
    </fieldset>
  );

  const renderDrawTab = () => (
    <div
      className='digital-signature-modal__draw-container'
      data-testid={`${baseTestId}-draw`}>
      <label
        className='ds-u-visibility--screen-reader'
        htmlFor='signature-canvas-wrapper'>
        Draw your signature using mouse or touch
      </label>
      <div
        ref={canvasWrapperRef}
        aria-describedby={canvasError ? 'canvas-error-message' : 'canvas-instructions'}
        aria-invalid={!!canvasError}
        aria-label='Signature drawing area'
        className={classnames('digital-signature-modal__canvas-wrapper', {
          'digital-signature-modal__canvas-wrapper--error': !!canvasError,
        })}
        data-testid={`${baseTestId}-canvas-wrapper`}
        id='signature-canvas-wrapper'
        role='img'
        tabIndex={0}>
        <span
          className='ds-u-visibility--screen-reader'
          id='canvas-instructions'>
          Use your mouse or touch screen to draw your signature in this area. Press Clear button to start over.
        </span>
        <SignatureCanvas
          ref={signatureCanvasRef}
          backgroundColor='white'
          canvasProps={{
            className: 'digital-signature-modal__canvas',
            height: canvasDimensions.bitmapHeight,
            style: {
              height: '100%',
              width: '100%',
            },
            width: canvasDimensions.bitmapWidth,
          }}
          penColor='black'
          onBegin={handleCanvasBeginStroke}
          onEnd={handleCanvasEndStroke}
        />
      </div>
      <div
        className='digital-signature-modal__canvas-actions'
        data-testid={`${baseTestId}-canvas-actions`}>
        {canvasError && (
          <>
            <span
              className='sr-only'
              id='canvas-error-message'
              role='alert'>
              {canvasError}
            </span>
            <Tooltip
              isError
              data-testid={`${baseTestId}-canvas-error`}
              placement='top'
              title={canvasError}>
              <ErrorIcon
                ariaLabel={canvasError}
                size={1.25}
              />
            </Tooltip>
          </>
        )}
        <Button
          bordered
          isAutoWidth
          data-testid={`${baseTestId}-clear`}
          disabled={!hasCanvasInput}
          type='button'
          variation={ButtonVariation.GHOST}
          onClick={handleClearCanvas}>
          Clear
        </Button>
      </div>
    </div>
  );

  const tabs: Tab[] = [
    {
      content: renderStyleTab(),
      id: 'select-style',
      label: 'Select Style',
    },
    {
      content: renderDrawTab(),
      id: 'draw',
      label: 'Draw',
    },
  ];

  return (
    <Modal
      className='digital-signature-modal-wrapper'
      data-testid={baseTestId}
      heading='Adopt Your Signature'
      isOpen={isOpen}
      size={ModalSize.Large}
      onClose={onClose}>
      <div
        className='digital-signature-modal'
        data-testid={`${baseTestId}-content`}>
        <p
          className='digital-signature-modal__description'
          data-testid={`${baseTestId}-description`}>
          Confirm your name and signature.
        </p>
        <RequiredFieldsLegend data-testid={`${baseTestId}-required-fields`} />

        {submitError ? (
          <Alert
            className='digital-signature-modal__submit-error'
            data-testid={`${baseTestId}-submit-error`}
            role='alert'
            variation='error'>
            {submitError}
          </Alert>
        ) : null}

        <div
          className='digital-signature-modal__name-field'
          data-testid={`${baseTestId}-name-field`}>
          <TextField
            disabled
            required
            data-testid={`${baseTestId}-full-name`}
            errorMessage={fullNameError}
            label='Full Name'
            name='signature-full-name'
            value={normalizedFullName}
          />
          {fullNameError ? (
            <p
              className='digital-signature-modal__field-error'
              data-testid={`${baseTestId}-full-name-error`}
              role='alert'>
              {fullNameError}
            </p>
          ) : null}
        </div>

        <Tabs
          className='digital-signature-modal__tabs'
          data-testid={`${baseTestId}-tabs`}
          selectedId={selectedTab}
          tabs={tabs}
          onChange={handleTabChange}
        />

        <div
          className='digital-signature-modal__preview-section'
          data-testid={`${baseTestId}-preview`}>
          <p
            className='digital-signature-modal__preview-label'
            data-testid={`${baseTestId}-preview-label`}>
            Preview
          </p>
          <div
            className='digital-signature-modal__preview-box'
            data-testid={`${baseTestId}-preview-box`}>
            <p
              className='digital-signature-modal__preview-signed-by'
              data-testid={`${baseTestId}-preview-signed-by`}>
              Signed by:
            </p>
            {selectedTab === 'draw' ? (
              drawnSignatureImage ? (
                <img
                  alt='Your drawn signature'
                  className='digital-signature-modal__preview-drawn-signature'
                  data-testid={`${baseTestId}-preview-drawn`}
                  src={drawnSignatureImage}
                />
              ) : (
                <p
                  className='digital-signature-modal__preview-placeholder'
                  data-testid={`${baseTestId}-preview-placeholder`}>
                  Draw your signature above
                </p>
              )
            ) : (
              renderPreview()
            )}
            <p
              className='digital-signature-modal__preview-date'
              data-testid={`${baseTestId}-preview-date`}>
              {formattedDate}
            </p>
          </div>
        </div>

        <div
          className='digital-signature-modal__disclaimer'
          data-testid={`${baseTestId}-disclaimer`}>
          {disclaimerParagraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>

        <div
          className='digital-signature-modal__actions'
          data-testid={`${baseTestId}-actions`}>
          <Button
            bordered
            isAutoWidth
            data-testid={`${baseTestId}-cancel`}
            type='button'
            onClick={onClose}>
            Cancel
          </Button>
          <Button
            isAutoWidth
            data-testid={`${baseTestId}-submit`}
            isLoading={isSubmitting}
            loadingText='Submitting...'
            type='button'
            variation={ButtonVariation.SOLID}
            onClick={handleAdoptAndSign}>
            Sign and Submit
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default DigitalSignatureModal;
